import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ItemContainer } from "@egalregal/shared";
import { useT } from "../../shared/i18n";
import { AddIcon, RemoveIcon } from "../../shared/components/icons";

/** The single-placement quantity editor controls (scan-followups 04). */
export interface PlacementQuantityEditor {
  /** The absolute total shown right now. */
  value: number;
  /** True while a write is in flight, so the stepper cannot double-fire. */
  saving: boolean;
  /** Writes the requested absolute total immediately; callers floor at 1. */
  onChange: (next: number) => void;
}

/** The containers an item lives in, shown by name and full path (ticket 05);
 * each row navigating to that container. With exactly one placement the row
 * also carries the inline `+`/`−` stepper (scan-followups 04); several or no
 * placements keep the read-only list the scan surface owns selection for. */
export function ContainersSection({
  containers,
  onOpen,
  quantity,
  quantityError,
}: {
  containers: ItemContainer[];
  onOpen: (containerId: string) => void;
  /** Present only when the single placement may be edited. */
  quantity?: PlacementQuantityEditor;
  /** The inline write error, or null when the last write succeeded. */
  quantityError?: string | null;
}) {
  const t = useT();
  const single = containers.length === 1;
  return (
    <Box sx={{ mb: 3 }} data-testid="item-containers">
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("itemContainers")}
      </Typography>
      {containers.length > 0 ? (
        <List dense disablePadding>
          {containers.map((link) => (
            <ListItem key={link.containerId} disablePadding>
              <ListItemButton sx={{ flex: 1, minWidth: 0 }} onClick={() => onOpen(link.containerId)}>
                <ListItemText
                  primary={link.name}
                  secondary={[link.path, !(single && quantity) ? `× ${link.quantity}` : null]
                    .filter(Boolean)
                    .join(" · ")}
                />
              </ListItemButton>
              {single && quantity && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0}
                  data-testid="item-placement-quantity"
                  sx={{ flexShrink: 0 }}
                >
                  <IconButton
                    size="small"
                    aria-label={t("decrementQuantity")}
                    disabled={quantity.saving || quantity.value <= 1}
                    onClick={() => quantity.onChange(quantity.value - 1)}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography
                    variant="body2"
                    data-testid="item-placement-quantity-value"
                    sx={{ minWidth: 24, textAlign: "center" }}
                  >
                    {quantity.value}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={t("incrementQuantity")}
                    disabled={quantity.saving}
                    onClick={() => quantity.onChange(quantity.value + 1)}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("itemNoContainers")}
        </Typography>
      )}
      {quantityError && (
        <Typography
          variant="body2"
          color="error"
          data-testid="item-placement-quantity-error"
          sx={{ mt: 0.5 }}
        >
          {quantityError}
        </Typography>
      )}
    </Box>
  );
}
